import React, { createContext, useContext, useState } from 'react'

import { GridValidRowModel } from '@mui/x-data-grid-premium'
import { EntityId } from '@reduxjs/toolkit'

export type RunsheetShiftTableContextType = {
  selectedStop: GridValidRowModel | null
  setSelectedStop: (row: GridValidRowModel | null) => void
  shiftId: EntityId
  setShiftId: (shiftId: EntityId) => void
  runsheetId: EntityId
  setRunsheetId: (runsheetId: EntityId) => void
}

export const RunsheetShiftTableContext = createContext<RunsheetShiftTableContextType>({
  selectedStop: null,
  setSelectedStop: () => { },
  shiftId: 0,
  setShiftId: () => { },
  runsheetId: 0,
  setRunsheetId: () => { },
})

export const useRunsheetShiftTableContext = () => {
  const context = useContext(RunsheetShiftTableContext)

  if (context === undefined) {
    throw new Error(
      'useRunsheetShiftTableContext must be used within a RunsheetShiftTableContextProvider',
    )
  }

  return context
}

/**
 * The RunsheetShiftTableContextProvider is a context provider that help share the state
 *    between the RunsheetShiftTable and the RunsheetShiftWindowToolbar components through
 *    the Mosaic Window rendering. Thus, any local actions or state that need to be shared
 *    between these two components should be managed here.
 */
export type RunsheetShiftTableContextProps = {
  children?: React.ReactNode
}

export const RunsheetShiftTableContextProvider: React.FC<RunsheetShiftTableContextProps> = ({ children }) => {
  const [selectedStop, setSelectedStop] = useState<GridValidRowModel | null>(null)
  const [shiftId, setShiftId] = useState<EntityId>(0)
  const [runsheetId, setRunsheetId] = useState<EntityId>(0)

  // Memoize the context value to prevent unnecessary re-renders
  const memoContextValue = React.useMemo(() => ({
    selectedStop,
    setSelectedStop,
    shiftId,
    setShiftId,
    runsheetId,
    setRunsheetId,
  }), [selectedStop, setSelectedStop, shiftId, setShiftId, runsheetId, setRunsheetId])

  return (
    <RunsheetShiftTableContext.Provider value={memoContextValue}>
      {children}
    </RunsheetShiftTableContext.Provider>
  )
}

